import { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './TreasuryDialog.css';

const dialogs = [];
let restoreBackground;

const focusableElements = element => Array.from(element.querySelectorAll(
    'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]'
)).filter(node => !node.closest('[hidden], [inert]') && getComputedStyle(node).display !== 'none' && getComputedStyle(node).visibility !== 'hidden');

function updateLayers() {
    dialogs.forEach((dialog, index) => {
        const covered = index !== dialogs.length - 1;
        dialog.layer.style.zIndex = String(2000 + index);
        dialog.layer.toggleAttribute('inert', covered);
        if (covered) dialog.layer.setAttribute('aria-hidden', 'true');
        else dialog.layer.removeAttribute('aria-hidden');
    });
}

function lockBackground() {
    const x = window.scrollX;
    const y = window.scrollY;
    const style = document.body.style;
    const previous = { position: style.position, top: style.top, left: style.left, width: style.width, overflow: style.overflow };
    const background = Array.from(document.body.children).filter(node => !node.classList.contains('treasury-dialog-layer'));
    const attributes = background.map(node => ({ node, inert: node.hasAttribute('inert'), hidden: node.getAttribute('aria-hidden') }));
    background.forEach(node => { node.setAttribute('inert', ''); node.setAttribute('aria-hidden', 'true'); });
    Object.assign(style, { position: 'fixed', top: `-${y}px`, left: `-${x}px`, width: '100%', overflow: 'hidden' });
    return () => {
        Object.assign(style, previous);
        attributes.forEach(({ node, inert, hidden }) => {
            node.toggleAttribute('inert', inert);
            if (hidden === null) node.removeAttribute('aria-hidden');
            else node.setAttribute('aria-hidden', hidden);
        });
        window.scrollTo(x, y);
    };
}

/** Mounted only while open. The stack keeps nested receipts/payment editors independent. */
export default function TreasuryDialog({ children, label, onClose, closeDisabled = false, className = '' }) {
    const layer = useRef(null);
    const panel = useRef(null);
    const latest = useRef({ onClose, closeDisabled });
    latest.current = { onClose, closeDisabled };

    useLayoutEffect(() => {
        const trigger = document.activeElement;
        const entry = { layer: layer.current, panel: panel.current };
        if (!dialogs.length) restoreBackground = lockBackground();
        dialogs.push(entry);
        updateLayers();
        panel.current.focus({ preventScroll: true });

        const keydown = event => {
            if (dialogs[dialogs.length - 1] !== entry) return;
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                if (!latest.current.closeDisabled) latest.current.onClose();
            } else if (event.key === 'Tab') {
                const items = focusableElements(entry.panel);
                const first = items[0];
                const last = items[items.length - 1];
                if (!first) {
                    event.preventDefault();
                    entry.panel.focus({ preventScroll: true });
                } else if (!entry.panel.contains(document.activeElement) || document.activeElement === entry.panel ||
                    (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
                    event.preventDefault();
                    (event.shiftKey ? last : first).focus({ preventScroll: true });
                }
            }
        };
        document.addEventListener('keydown', keydown);
        return () => {
            document.removeEventListener('keydown', keydown);
            dialogs.splice(dialogs.indexOf(entry), 1);
            updateLayers();
            if (!dialogs.length) {
                restoreBackground?.();
                restoreBackground = null;
            }
            if (trigger?.isConnected && !trigger.closest('[inert]')) trigger.focus({ preventScroll: true });
        };
    }, []);

    useEffect(() => {
        // visualViewport also shrinks when the software keyboard covers the layout viewport.
        const viewport = window.visualViewport;
        const update = () => {
            if (!viewport || !layer.current) return;
            layer.current.style.setProperty('--treasury-viewport-height', `${viewport.height}px`);
            layer.current.style.setProperty('--treasury-viewport-top', `${viewport.offsetTop}px`);
        };
        update();
        viewport?.addEventListener('resize', update);
        viewport?.addEventListener('scroll', update);
        return () => {
            viewport?.removeEventListener('resize', update);
            viewport?.removeEventListener('scroll', update);
        };
    }, []);

    return createPortal(
        <div ref={layer} className="treasury-scope treasury-dialog-layer" onClick={event => {
            if (event.target === event.currentTarget && !closeDisabled) onClose();
        }}>
            <div ref={panel} className={`${className} treasury-dialog`} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1}>
                {children}
            </div>
        </div>, document.body
    );
}
